import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/20/solid';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import SelectDropdown from './select';
import StatusPill from './StatusPill';

type SortDirection = 'asc' | 'desc' | null;

interface TableColumn {
  label: string;
  accessor: string;
  render?: (value: any, item?:any) => React.ReactNode;
  sortable?: boolean;
  minWidth?: string;
}

interface TableProps {
  columns: TableColumn[];
  data: any[];
  totalItems?: number;
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onRowClick?: (item: any) => void;
  makeRowClickable?: boolean; // Opt-in to make entire row clickable
  header?: string;
  searchable?: boolean;
  onSearch?: (searchTerm: string) => void;
  onStatusChange?: (userId: string, newStatus: string) => void;
  isEditMode?: boolean;
  testId?: string;
  onSort?: (key: string, direction: SortDirection) => void;
  sortConfig?: { key: string; direction: SortDirection };
}

const Table: React.FC<TableProps> = ({
  columns,
  data,
  totalItems,
  currentPage = 1,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  makeRowClickable = false,
  header,
  searchable,
  onSearch,
  onStatusChange,
  isEditMode = false,
  testId = 'table',
  onSort,
  sortConfig: externalSortConfig
}) => {
  const { isOnline } = useNetworkStatus();
  const showPagination = totalItems !== undefined && onPageChange !== undefined;

  // Page size options
  const pageSizeOptions = [
    { value: '10', label: '10' },
    { value: '25', label: '25' },
    { value: '50', label: '50' },
    { value: '100', label: '100' }
  ];

  // Find the first sortable column's accessor or default to the first column
  const defaultSortKey = columns.find(col => col.sortable)?.accessor || columns[0]?.accessor || '';
  
  const [localSortConfig, setLocalSortConfig] = useState<{ key: string; direction: SortDirection }>({
    key: defaultSortKey,
    direction: null
  });

  // Use external sortConfig if provided, otherwise use local state
  const effectiveSortConfig = externalSortConfig || localSortConfig;

  const requestSort = (key: string) => {
    let direction: SortDirection = null;
    
    if (key !== effectiveSortConfig.key) {
      direction = 'asc';
    } else {
      if (effectiveSortConfig.direction === 'asc') {
        direction = 'desc';
      } else if (effectiveSortConfig.direction === 'desc') {
        direction = null;
      } else {
        direction = 'asc';
      }
    }
    
    if (!externalSortConfig) {
      setLocalSortConfig({ key, direction });
    }
    
    if (onSort) {
      onSort(key, direction);
    }
  };

  // Calculate page numbers to display
  const getPageNumbers = () => {
    if (!showPagination || !totalItems) return [];
    
    const totalPages = Math.ceil(totalItems / pageSize);
    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const handleNextPage = () => {
    if (showPagination && onPageChange && totalItems) {
      const totalPages = Math.ceil(totalItems / pageSize);
      onPageChange(Math.min(currentPage + 1, totalPages));
    }
  };

  const handlePreviousPage = () => {
    if (showPagination && onPageChange) {
      onPageChange(Math.max(currentPage - 1, 1));
    }
  };

  const handleFirstPage = () => {
    if (showPagination && onPageChange) {
      onPageChange(1);
    }
  };

  const handleLastPage = () => {
    if (showPagination && onPageChange && totalItems) {
      const totalPages = Math.ceil(totalItems / pageSize);
      onPageChange(totalPages);
    }
  };

  const renderCell = (column: TableColumn, item: any) => {
    if (column.render) {
      return column.render(item[column.accessor], item);
    }

    if (column.accessor === 'status' && typeof item[column.accessor] !== 'object') {
      return (
        <div className="flex justify-center">
          <StatusPill status={item[column.accessor]} />
        </div>
      );
    }
    return item[column.accessor];
  };

  const getSortIcon = (columnAccessor: string) => {
    if (!columns.find(col => col.accessor === columnAccessor)?.sortable) {
      return null;
    }

    if (effectiveSortConfig.key === columnAccessor) {
      if (effectiveSortConfig.direction === 'asc') {
        return (
          <div className="inline-flex items-center gap-1 ml-1" style={{ width: '20px' }}>
             <img src="/arrow-up.svg" alt="Sort Ascending" className="w-3 h-3" />
          </div>
        );
      }
      if (effectiveSortConfig.direction === 'desc') {
        return (
          <div className="inline-flex items-center gap-1 ml-1" style={{ width: '20px' }}>
             <img src="/arrow-down.svg" alt="Sort Descending" className="w-3 h-3" />
          </div>
        );
      }
    }
    return (
      <div className="inline-flex items-center gap-0.5 ml-1" style={{ width: '20px' }}>
      <img src="/sort-alt.svg" alt="Sort Icon" className="w-3 h-3" />
      </div>
    );
  };

  const getColumnWidthClass = (accessor: string): string => {
    switch (accessor) {
      case 'status':
        return 'text-center min-w-[120px]';
      case 'submittedBy':
        return 'text-center min-w-[150px]';
      case 'completion':
        return 'text-center min-w-[120px]';
      case 'name':
        return 'min-w-[200px]';
      case 'role':
        return 'min-w-[180px]';
      case 'lastLogin':
        return 'min-w-[150px]';
      default:
        return 'min-w-[100px]';
    }
  };

  const getCellClassName = (accessor: string): string => {
    const baseClasses = 'px-4 sm:px-6 py-2 text-[14px] sm:text-[16px] text-gray-900 whitespace-nowrap';
    
    switch (accessor) {
      case 'status':
      case 'submittedBy':
      case 'completion':
        return `${baseClasses} text-center`;
      case 'name':
        return `${baseClasses} cursor-pointer hover:text-[#A42823] group-hover:text-[#A42823]`;
      default:
        return baseClasses;
    }
  };

  return (
    <div className="bg-white rounded-lg w-full" id={`${testId}-container`}>
      {onPageSizeChange && (
        <div className="flex items-center justify-start px-4 pb-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700 whitespace-nowrap">Rows per page:</span>
            <div className="w-20">
              <SelectDropdown
                options={pageSizeOptions}
                onSelect={(value) => onPageSizeChange(Number(value))}
                getValue={(option) => option.value}
                getDisplayText={(option) => option.label}
                selectedValue={pageSize.toString()}
                testId={`${testId}-page-size-top`}
              />
            </div>
          </div>
        </div>
      )}
      <div className="w-full overflow-x-auto min-w-0 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        <table className="min-w-full table-auto" id={`${testId}-data`}>
          <thead className="bg-white border-b border-[#E8E8E8]">
            <tr>
              {columns.map((column, index) => (
                <th
                  key={index}
                  id={`${testId}-header-${column.accessor}`}
                  style={{ minWidth: column.minWidth || 'auto' }}
                  className={`px-4 sm:px-6 py-4 text-[14px] sm:text-[16px] font-bold text-[#000000] tracking-wider whitespace-nowrap ${getColumnWidthClass(column.accessor)} ${column.sortable ? 'cursor-pointer select-none hover:text-gray-600' : ''}`}
                  onClick={() => column.sortable ? requestSort(column.accessor) : null}
                >
                  <div className={`flex items-center gap-1 ${
                    column.accessor === 'status' || column.accessor === 'submittedBy' || column.accessor === 'completion' ? 'justify-center' : ''
                  }`}>
                    {column.label}
                    {column.sortable && getSortIcon(column.accessor)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((item, index) => (
              <tr
                key={index}
                id={`${testId}-row-${index}`}
                onClick={() => makeRowClickable && onRowClick && onRowClick(item)}
                className={`h-[3.2rem] hover:bg-[#f9fafb] transition-colors duration-150 ${makeRowClickable && onRowClick ? 'cursor-pointer group' : ''}`}
              >
                {columns.map((column, idx) => (
                  <td
                    key={idx}
                    id={`${testId}-cell-${index}-${column.accessor}`}
                    onClick={(e) => {
                      // If row is not clickable, allow individual cell clicks for 'name' column
                      if (!makeRowClickable && column.accessor === 'name' && onRowClick) {
                        e.stopPropagation();
                        onRowClick(item);
                      }
                    }}
                    className={getCellClassName(column.accessor)}
                  >
                    {renderCell(column, item)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {Boolean(showPagination && isOnline && totalItems) && (
        <div className="mt-4 flex flex-col md:flex-row justify-between items-center px-4 py-3 border-t border-gray-200 gap-4 sm:gap-0">
          <div className="flex items-center gap-3">
            {onPageSizeChange && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700 whitespace-nowrap">Rows per page:</span>
                <div className="w-20">
                  <SelectDropdown
                    options={pageSizeOptions}
                    onSelect={(value) => onPageSizeChange(Number(value))}
                    getValue={(option) => option.value}
                    getDisplayText={(option) => option.label}
                    selectedValue={pageSize.toString()}
                    testId={`${testId}-page-size-bottom`}
                    dropdownContainerClassName="bottom-full mb-1"
                  />
                </div>
              </div>
            )}
            <div id={`${testId}-pagination-info`} className="text-sm text-gray-700 whitespace-nowrap">
              Showing {Math.min((currentPage - 1) * pageSize + 1, totalItems || 0)} -{' '}
              {Math.min(currentPage * pageSize, totalItems || 0)} of {totalItems || 0}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              id={`${testId}-pagination-first`}
              onClick={handleFirstPage}
              disabled={currentPage === 1}
              className="p-1 rounded-full text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronDoubleLeftIcon className="h-5 w-5" />
            </button>
            <button
              id={`${testId}-pagination-previous`}
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className="p-1 rounded-full text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            
            {/* Page Numbers with responsive hiding */}
            <div className="hidden sm:flex space-x-1">
              {getPageNumbers().map((pageNum) => (
                <button
                  key={pageNum}
                  id={`${testId}-pagination-page-${pageNum}`}
                  onClick={() => onPageChange && onPageChange(pageNum)}
                  className={`w-8 h-8 rounded-lg ${
                    pageNum === currentPage
                      ? 'bg-[#FFE9E8] text-[#A42823]'
                      : 'text-gray-600 hover:bg-[#FFE9E8]'
                  }`}
                >
                  {pageNum}
                </button>
              ))}
            </div>

            {/* Current page indicator for mobile */}
            <span className="sm:hidden text-sm text-gray-700">
              Page {currentPage}
            </span>

            <button
              id={`${testId}-pagination-next`}
              onClick={handleNextPage}
              disabled={currentPage === Math.ceil((totalItems || 0) / pageSize)}
              className="p-1 rounded-full text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
            <button
              id={`${testId}-pagination-last`}
              onClick={handleLastPage}
              disabled={currentPage === Math.ceil((totalItems || 0) / pageSize)}
              className="p-1 rounded-full text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronDoubleRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Table;